// src/pages/dashboard/PayslipPreview.js
// IMPORTANT: Make sure Payroll.js includes 'employee_number' and 'position' in employees query!

import React, { useState, useEffect, useRef } from 'react';
import {
  ConfigProvider,
  Modal,
  Button,
  Typography,
  Divider,
  Space,
  Row,
  Col,
  message,
  Tag,
  Image
} from 'antd';
import { PrinterOutlined, DownloadOutlined, FileImageOutlined } from '@ant-design/icons';
import moment from 'moment';
import { supabase } from '../../services/supabase';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { formatHoursMinutes } from '../../services/attendanceUtils';

const { Title, Text } = Typography;

const PayslipPreview = ({ visible, employee, onClose }) => {
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [pngLoading, setPngLoading] = useState(false);
  const [deductions, setDeductions] = useState([]);
  const [allowances, setAllowances] = useState([]);
  const [leaveData, setLeaveData] = useState({
    sickLeave: 0,
    localLeave: 0,
    absent: 0,
    off: 0
  });
  const payslipRef = useRef(null);

  // Parse allowances from payroll record JSON
  const allowanceDetails = React.useMemo(() => {
    try {
      if (employee?.allowance_details) {
        const parsed = typeof employee.allowance_details === 'string'
          ? JSON.parse(employee.allowance_details)
          : employee.allowance_details;
        console.log('✅ Parsed allowance details from payroll record:', parsed);
        return Array.isArray(parsed) ? parsed : [];
      }
    } catch (e) {
      console.error('❌ Error parsing allowance details:', e);
    }
    return [];
  }, [employee?.allowance_details]);

  // Parse deductions from payroll record JSON
  const deductionDetails = React.useMemo(() => {
    try {
      if (employee?.deduction_details) {
        const parsed = typeof employee.deduction_details === 'string'
          ? JSON.parse(employee.deduction_details)
          : employee.deduction_details;
        console.log('✅ Parsed deduction details from payroll record:', parsed);
        return Array.isArray(parsed) ? parsed : [];
      }
    } catch (e) {
      console.error('❌ Error parsing deduction details:', e);
    }
    return [];
  }, [employee?.deduction_details]);

  useEffect(() => {
    if (visible && employee) {
      console.log('📄 Employee payroll data:', employee);
      console.log('🆔 Employee NIC (employee_number):', employee.employees?.employee_number);
      console.log('💰 Total allowances:', employee.total_allowances);
      console.log('📋 Allowance details array:', allowanceDetails);
      
      fetchCompanyData(employee.company_id);
      fetchDeductions();
      
      // Only fetch from DB if JSON is empty AND total_allowances > 0
      if (allowanceDetails.length === 0 && employee.total_allowances > 0) {
        console.log('⚠️ Allowance details JSON is empty but total exists, fetching from DB...');
        fetchAllowances();
      }
      
      fetchLeaveData();
    }
  }, [visible, employee]);

  const fetchCompanyData = async (company_id) => {
    if (!company_id) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('companies')
        .select('name, logo_url, address, registration_number, tax_number')
        .eq('id', company_id)
        .single();

      if (error) throw error;
      setCompanyData(data);
    } catch (error) {
      console.error('Error fetching company data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch deductions
  const fetchDeductions = async () => {
    if (!employee?.employee_id || !employee?.company_id) return;

    try {
      const { data, error } = await supabase
        .from('employee_deductions')
        .select(`
          id,
          amount,
          start_date,
          end_date,
          deduction_type_id,
          deduction_types!employee_deductions_deduction_type_id_fkey (
            id,
            name,
            is_percentage
          )
        `)
        .eq('employee_id', employee.employee_id)
        .eq('company_id', employee.company_id);

      if (error) throw error;

      console.log('✅ Fetched deductions:', data);

      const validDeductions = (data || []).filter(deduction => {
        return deduction.deduction_types && deduction.deduction_types.name;
      });

      setDeductions(validDeductions);
    } catch (error) {
      console.error('Error fetching deductions:', error);
      setDeductions([]);
    }
  };

  // Fetch allowances from DB if JSON is empty (fallback)
  const fetchAllowances = async () => {
    if (!employee?.employee_id || !employee?.company_id) return;

    try {
      console.log('🔍 Fetching allowances from DB for employee:', employee.employee_id);
      
      const { data, error } = await supabase
        .from('employee_allowances')
        .select(`
          id,
          amount,
          start_date,
          end_date,
          allowance_type_id,
          allowance_types!employee_allowances_allowance_type_id_fkey (
            id,
            name,
            is_percentage,
            calculation_method
          )
        `)
        .eq('employee_id', employee.employee_id)
        .eq('company_id', employee.company_id);

      if (error) throw error;

      console.log('✅ Fetched allowances from DB:', data);

      // Filter for active allowances during payroll period
      const activeAllowances = (data || []).filter(allowance => {
        if (!allowance.start_date || !allowance.end_date) return false;
        
        const allowanceStart = moment(allowance.start_date);
        const allowanceEnd = moment(allowance.end_date);
        const periodStart = moment(employee.period_start);
        const periodEnd = moment(employee.period_end);
        
        return allowanceStart.isSameOrBefore(periodEnd) && allowanceEnd.isSameOrAfter(periodStart);
      });

      console.log('✅ Active allowances:', activeAllowances);
      setAllowances(activeAllowances);
    } catch (error) {
      console.error('Error fetching allowances:', error);
      setAllowances([]);
    }
  };

  // Fetch leave data from timesheet_daily_records
  const fetchLeaveData = async () => {
    if (!employee?.employee_id || !employee?.period_start || !employee?.period_end) return;

    try {
      const { data, error } = await supabase
        .from('timesheet_daily_records')
        .select('leave_type')
        .eq('employee_id', employee.employee_id)
        .eq('company_id', employee.company_id)
        .gte('date', employee.period_start)
        .lte('date', employee.period_end)
        .not('leave_type', 'is', null);

      if (error) throw error;

      const counts = {
        sickLeave: 0,
        localLeave: 0,
        absent: 0,
        off: 0
      };

      (data || []).forEach(record => {
        switch (record.leave_type) {
          case 'SICK LEAVE':
            counts.sickLeave++;
            break;
          case 'LOCAL LEAVE':
            counts.localLeave++;
            break;
          case 'ABSENT':
            counts.absent++;
            break;
          case 'OFF':
            counts.off++;
            break;
        }
      });

      // FIXED: Calculate absent days from working days if not found in timesheet
      if (counts.absent === 0 && employee?.calculation_metadata) {
        try {
          const metadata = typeof employee.calculation_metadata === 'string'
            ? JSON.parse(employee.calculation_metadata)
            : employee.calculation_metadata;

          const workingDays = metadata?.working_days || 0;

          // Calculate total working days in period (excluding weekends)
          const periodStart = moment(employee.period_start);
          const periodEnd = moment(employee.period_end);
          let totalWorkingDays = 0;

          // Count working days (Mon-Sat, excluding Sunday)
          for (let d = moment(periodStart); d.isSameOrBefore(periodEnd); d.add(1, 'day')) {
            if (d.day() !== 0) { // 0 = Sunday
              totalWorkingDays++;
            }
          }

          // Absent days = total working days - days worked - sick leave - local leave - off days
          const absentDays = Math.max(0, totalWorkingDays - workingDays - counts.sickLeave - counts.localLeave - counts.off);
          counts.absent = absentDays;

          console.log('📊 Calculated absent days:', {
            totalWorkingDays,
            workingDays,
            sickLeave: counts.sickLeave,
            localLeave: counts.localLeave,
            off: counts.off,
            absentDays
          });
        } catch (e) {
          console.error('Error calculating absent days:', e);
        }
      }

      setLeaveData(counts);
    } catch (error) {
      console.error('Error fetching leave data:', error);
      setLeaveData({ sickLeave: 0, localLeave: 0, absent: 0, off: 0 });
    }
  };

  const handlePrint = () => {
  const printContent = document.getElementById('payslip-content');
  const originalContent = document.body.innerHTML;
  
  // Create a new window for printing with white background
  const printWindow = document.createElement('div');
  printWindow.innerHTML = `
    <style>
      @media print {
        body {
          background-color: white !important;
          margin: 0;
          padding: 0;
        }
        @page {
          margin: 0;
          size: A4;
        }
      }
    </style>
    ${printContent.innerHTML}
  `;
  
  document.body.innerHTML = printWindow.innerHTML;
  window.print();
  document.body.innerHTML = originalContent;
  window.location.reload();
};

  const handleDownloadPDF = async () => {
    try {
      setDownloadLoading(true);

      const content = payslipRef.current;
      if (!content) {
        message.error('Could not find payslip content');
        return;
      }

      const canvas = await html2canvas(content, {
        scale: 2,
        useCORS: true,
        logging: false,
        letterRendering: true
      });

      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      const pdf = new jsPDF('p', 'mm', 'a4');

      pdf.addImage(
        canvas.toDataURL('image/png'),
        'PNG',
        0,
        0,
        imgWidth,
        imgHeight,
        '',
        'FAST'
      );

      const employeeName = `${employee.employees?.first_name || ''}_${employee.employees?.last_name || ''}`.replace(/\s+/g, '_');
      const periodEnd = employee.period_end ?
        moment(employee.period_end).format('YYYY-MM-DD') : 'payslip';
      pdf.save(`${employeeName}_${periodEnd}.pdf`);

      message.success('Payslip downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      message.error('Failed to download PDF');
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleDownloadPNG = async () => {
    try {
      setPngLoading(true);

      const content = payslipRef.current;
      if (!content) {
        message.error('Could not find payslip content');
        return;
      }

      const canvas = await html2canvas(content, {
        scale: 2,
        useCORS: true,
        logging: false,
        letterRendering: true
      });

      // Convert canvas to blob
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const employeeName = `${employee.employees?.first_name || ''}_${employee.employees?.last_name || ''}`.replace(/\s+/g, '_');
        const periodEnd = employee.period_end ?
          moment(employee.period_end).format('YYYY-MM-DD') : 'payslip';

        link.href = url;
        link.download = `${employeeName}_${periodEnd}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        message.success('Payslip saved as PNG successfully');
      }, 'image/png');

    } catch (error) {
      console.error('Error generating PNG:', error);
      message.error('Failed to save as PNG');
    } finally {
      setPngLoading(false);
    }
  };

  // Calculate allowance amount for fetched allowances
  const calculateAllowanceAmount = (allowance, workingDays) => {
    const calculationMethod = allowance.allowance_types?.calculation_method || 'fixed';
    
    if (calculationMethod === 'percentage') {
      return totalEarnings * (allowance.amount / 100);
    } else if (calculationMethod === 'per_day') {
      return allowance.amount * workingDays;
    }
    return allowance.amount;
  };

  // Format currency with commas
  const formatCurrency = (amount) => {
    return amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  if (!employee) return null;

  // Calculate totals
  const totalEarnings = (
    (employee.regular_amount || 0) + 
    (employee.overtime_amount || 0) + 
    (employee.double_overtime_amount || 0) + 
    (employee.holiday_amount || 0)
  );

  const totalAllowances = employee.total_allowances || 0;
  const totalDeductions = employee.total_deductions || 0;

  // Calculate deduction amount on BASE EARNINGS
  const calculateDeductionAmount = (deduction) => {
    if (deduction.deduction_types?.is_percentage) {
      return totalEarnings * deduction.amount / 100;
    }
    return deduction.amount;
  };

  // Get working days from calculation_metadata
  let workingDays = 0;
  try {
    if (employee?.calculation_metadata) {
      const metadata = typeof employee.calculation_metadata === 'string' 
        ? JSON.parse(employee.calculation_metadata)
        : employee.calculation_metadata;
      workingDays = metadata?.working_days || 0;
    }
  } catch (e) {
    console.error('Error parsing working days:', e);
  }

  // Determine which allowances to display - prioritize JSON, fallback to DB
  const displayAllowances = allowanceDetails.length > 0 
    ? allowanceDetails 
    : allowances.map(a => ({
        name: a.allowance_types?.name,
        amount: calculateAllowanceAmount(a, workingDays),
        is_percentage: a.allowance_types?.is_percentage,
        calculation_method: a.allowance_types?.calculation_method,
        original_amount: a.amount,
        working_days: workingDays
      }));

  return (
    <Modal
      title="Payslip Preview"
      open={visible}
      onCancel={onClose}
      width={900}
      footer={
        <div style={{ textAlign: 'center' }}>
          <Space>
            <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
              Print
            </Button>
            <Button
              key="download"
              icon={<DownloadOutlined />}
              onClick={handleDownloadPDF}
              loading={downloadLoading}
            >
              Download PDF
            </Button>
            <Button
              key="png"
              icon={<FileImageOutlined />}
              onClick={handleDownloadPNG}
              loading={pngLoading}
            >
              Save as PNG
            </Button>
            <Button key="close" onClick={onClose}>
              Close
            </Button>
          </Space>
        </div>
      }
    >
      {/* The payslip is a printed document: keep antd's default look inside it so
          PDF/PNG exports stay identical whatever theme the app uses */}
      <ConfigProvider theme={{ inherit: false }}>
      <div
        id="payslip-content"
        ref={payslipRef}
        style={{
          padding: '24px',
          backgroundColor: '#F5F1ED',
          minHeight: '220mm',
          width: '210mm',
          margin: '0 auto',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Inter", sans-serif'
        }}
      >
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #8B8B8D 0%, #A5A3A6 100%)',
          padding: '14px 20px',
          borderRadius: '2px',
          marginBottom: '14px',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: 'linear-gradient(90deg, #C5C3C6 0%, #8B8B8D 100%)'
          }}></div>

          {companyData?.logo_url && (
            <div style={{ marginBottom: '8px' }}>
              <Image
                src={companyData.logo_url}
                alt="Company Logo"
                preview={false}
                height={60}
                style={{ objectFit: 'contain' }}
              />
            </div>
          )}
          <Title level={2} style={{
            color: '#FFFFFF',
            margin: 0,
            fontSize: '24px',
            fontWeight: '300',
            letterSpacing: '2px',
            textTransform: 'uppercase'
          }}>
            {companyData?.name || 'Company Name'}
          </Title>
        </div>

        <div style={{ background: 'white', padding: '16px 18px', borderRadius: '2px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)' }}>
          {/* Title */}
          <Title level={3} style={{
            textAlign: 'center',
            color: '#6B6B6D',
            margin: '0 0 10px 0',
            fontSize: '18px',
            fontWeight: '300',
            letterSpacing: '4px',
            textTransform: 'uppercase',
            borderBottom: '1px solid #E8E6E3',
            paddingBottom: '8px'
          }}>
            Payslip
          </Title>

          {/* Employee & Period Info */}
          <Row gutter={16} style={{ marginBottom: '12px' }}>
            <Col span={12}>
              <div style={{
                border: '1px solid #E8E6E3',
                borderRadius: '2px',
                padding: '10px',
                background: '#FAFAFA',
                height: '100%'
              }}>
                <Text strong style={{
                  color: '#8B8B8D',
                  fontSize: '10px',
                  display: 'block',
                  marginBottom: '6px',
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase'
                }}>
                  Employee Details
                </Text>
                <Space direction="vertical" size={3} style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <Text style={{ fontSize: '11px', color: '#999', fontWeight: '300' }}>Name</Text>
                    <Text strong style={{ fontSize: '12px', color: '#4A4A4C', fontWeight: '400' }}>
                      {employee.employees?.first_name} {employee.employees?.last_name}
                    </Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <Text style={{ fontSize: '11px', color: '#999', fontWeight: '300' }}>NIC</Text>
                    <Text strong style={{ fontSize: '12px', color: '#4A4A4C', fontWeight: '400' }}>
                      {employee.employees?.employee_number || 'N/A'}
                    </Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <Text style={{ fontSize: '11px', color: '#999', fontWeight: '300' }}>Position</Text>
                    <Text style={{ fontSize: '12px', color: '#6B6B6D', fontWeight: '300' }}>
                      {employee.employees?.position || 'N/A'}
                    </Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <Text style={{ fontSize: '11px', color: '#999', fontWeight: '300' }}>Department</Text>
                    <Text style={{ fontSize: '12px', color: '#6B6B6D', fontWeight: '300' }}>
                      {employee.employees?.department || 'N/A'}
                    </Text>
                  </div>
                </Space>
              </div>
            </Col>

            <Col span={12}>
              <div style={{
                border: '1px solid #E8E6E3',
                borderRadius: '2px',
                padding: '10px',
                background: '#FAFAFA',
                height: '100%'
              }}>
                <Text strong style={{
                  color: '#8B8B8D',
                  fontSize: '10px',
                  display: 'block',
                  marginBottom: '6px',
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase'
                }}>
                  Pay Period
                </Text>
                <Space direction="vertical" size={3} style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <Text style={{ fontSize: '11px', color: '#999', fontWeight: '300' }}>From</Text>
                    <Text strong style={{ fontSize: '12px', color: '#4A4A4C', fontWeight: '400' }}>
                      {moment(employee.period_start).format('DD/MM/YYYY')}
                    </Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <Text style={{ fontSize: '11px', color: '#999', fontWeight: '300' }}>To</Text>
                    <Text strong style={{ fontSize: '12px', color: '#4A4A4C', fontWeight: '400' }}>
                      {moment(employee.period_end).format('DD/MM/YYYY')}
                    </Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <Text style={{ fontSize: '11px', color: '#999', fontWeight: '300' }}>Days Worked</Text>
                    <Text strong style={{ fontSize: '12px', color: '#4A4A4C', fontWeight: '400' }}>
                      {workingDays || 0}
                    </Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <Text style={{ fontSize: '11px', color: '#999', fontWeight: '300' }}>Absent</Text>
                    <Text strong style={{ fontSize: '12px', color: '#4A4A4C', fontWeight: '400' }}>
                      {leaveData.absent || 0}
                    </Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <Text style={{ fontSize: '11px', color: '#999', fontWeight: '300' }}>Status</Text>
                    <Tag
                      color={employee?.status === 'paid' ? 'success' : employee?.status === 'processed' ? 'processing' : 'default'}
                      style={{
                        fontSize: '10px',
                        padding: '2px 10px',
                        margin: 0,
                        border: 'none',
                        background: employee?.status === 'paid' ? '#C5C3C6' : '#E8E6E3',
                        color: employee?.status === 'paid' ? 'white' : '#6B6B6D'
                      }}
                    >
                      {employee?.status?.toUpperCase() || 'PENDING'}
                    </Tag>
                  </div>
                </Space>
              </div>
            </Col>
          </Row>

          {/* Leave Summary - Compact version */}
          {(leaveData.sickLeave > 0 || leaveData.localLeave > 0 || leaveData.off > 0) && (
            <div style={{
              background: '#F9F8F7',
              border: '1px solid #E8E6E3',
              borderRadius: '2px',
              padding: '6px 12px',
              marginBottom: '10px'
            }}>
              <Space size={12} wrap>
                {leaveData.sickLeave > 0 && (
                  <Text style={{ fontSize: '11px', color: '#6B6B6D', fontWeight: '300' }}>
                    Sick Leave: <strong style={{ color: '#4A4A4C' }}>{leaveData.sickLeave}</strong>
                  </Text>
                )}
                {leaveData.localLeave > 0 && (
                  <Text style={{ fontSize: '11px', color: '#6B6B6D', fontWeight: '300' }}>
                    Local Leave: <strong style={{ color: '#4A4A4C' }}>{leaveData.localLeave}</strong>
                  </Text>
                )}
                {leaveData.off > 0 && (
                  <Text style={{ fontSize: '11px', color: '#6B6B6D', fontWeight: '300' }}>
                    Day Off: <strong style={{ color: '#4A4A4C' }}>{leaveData.off}</strong>
                  </Text>
                )}
              </Space>
            </div>
          )}

          {/* Earnings */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{
              background: '#C5C3C6',
              padding: '6px 12px',
              borderRadius: '2px 2px 0 0'
            }}>
              <Text strong style={{
                color: 'white',
                fontSize: '10px',
                letterSpacing: '1.5px',
                textTransform: 'uppercase'
              }}>
                Earnings
              </Text>
            </div>
            <div style={{
              border: '1px solid #E8E6E3',
              borderTop: 'none',
              borderRadius: '0 0 2px 2px',
              padding: '8px 12px',
              background: 'white'
            }}>
              <Space direction="vertical" size={3} style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <Text style={{ fontSize: '11px', color: '#999', fontWeight: '300' }}>
                    Regular Hours ({formatHoursMinutes(employee.regular_hours || 0)})
                  </Text>
                  <Text style={{ fontSize: '12px', color: '#4A4A4C', fontWeight: '400' }}>
                    Rs {formatCurrency(employee.regular_amount || 0)}
                  </Text>
                </div>
                {employee.overtime_hours > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <Text style={{ fontSize: '11px', color: '#999', fontWeight: '300' }}>
                      Overtime ({formatHoursMinutes(employee.overtime_hours)})
                    </Text>
                    <Text style={{ fontSize: '12px', color: '#4A4A4C', fontWeight: '400' }}>
                      Rs {formatCurrency(employee.overtime_amount || 0)}
                    </Text>
                  </div>
                )}
                {employee.double_overtime_hours > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <Text style={{ fontSize: '11px', color: '#999', fontWeight: '300' }}>
                      Double OT ({formatHoursMinutes(employee.double_overtime_hours)})
                    </Text>
                    <Text style={{ fontSize: '12px', color: '#4A4A4C', fontWeight: '400' }}>
                      Rs {formatCurrency(employee.double_overtime_amount || 0)}
                    </Text>
                  </div>
                )}
                {employee.holiday_hours > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <Text style={{ fontSize: '11px', color: '#999', fontWeight: '300' }}>
                      Holiday ({formatHoursMinutes(employee.holiday_hours)})
                    </Text>
                    <Text style={{ fontSize: '12px', color: '#4A4A4C', fontWeight: '400' }}>
                      Rs {formatCurrency(employee.holiday_amount || 0)}
                    </Text>
                  </div>
                )}
                <Divider style={{ margin: '6px 0', borderColor: '#E8E6E3' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <Text strong style={{ fontSize: '12px', color: '#6B6B6D', fontWeight: '400' }}>Base Earnings</Text>
                  <Text strong style={{ fontSize: '13px', color: '#4A4A4C', fontWeight: '500' }}>
                    Rs {formatCurrency(totalEarnings)}
                  </Text>
                </div>
              </Space>
            </div>
          </div>

          {/* Allowances & Deductions Side by Side */}
          <Row gutter={16} style={{ marginBottom: '10px' }}>
            {/* Allowances */}
            <Col span={12}>
              <div style={{
                background: '#C5C3C6',
                padding: '6px 12px',
                borderRadius: '2px 2px 0 0'
              }}>
                <Text strong style={{
                  color: 'white',
                  fontSize: '10px',
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase'
                }}>
                  Allowances
                </Text>
              </div>
              <div style={{
                border: '1px solid #E8E6E3',
                borderTop: 'none',
                borderRadius: '0 0 2px 2px',
                padding: '8px 12px',
                minHeight: '70px',
                background: 'white'
              }}>
                {displayAllowances.length > 0 ? (
                  <Space direction="vertical" size={3} style={{ width: '100%' }}>
                    {displayAllowances.map((allowance, index) => {
                      let description = allowance.name || 'Allowance';

                      if (allowance.calculation_method === 'per_day' && allowance.working_days) {
                        description = `${allowance.name} (Rs ${allowance.original_amount || allowance.amount} × ${allowance.working_days} days)`;
                      } else if (allowance.is_percentage) {
                        description = `${allowance.name} (${allowance.original_amount}%)`;
                      } else if (allowance.calculation_method === 'fixed') {
                        description = allowance.name;
                      }

                      return (
                        <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Text style={{ fontSize: '11px', color: '#999', flex: 1, marginRight: '12px', fontWeight: '300' }}>
                            {description}
                          </Text>
                          <Text style={{ fontSize: '12px', color: '#8B8B8D', whiteSpace: 'nowrap', fontWeight: '400' }}>
                            + Rs {formatCurrency(allowance.amount || 0)}
                          </Text>
                        </div>
                      );
                    })}
                    <Divider style={{ margin: '6px 0', borderColor: '#E8E6E3' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <Text strong style={{ fontSize: '12px', color: '#6B6B6D', fontWeight: '400' }}>Total</Text>
                      <Text strong style={{ fontSize: '13px', color: '#8B8B8D', fontWeight: '500' }}>
                        + Rs {formatCurrency(totalAllowances)}
                      </Text>
                    </div>
                  </Space>
                ) : totalAllowances > 0 ? (
                  <Space direction="vertical" size={3} style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <Text style={{ fontSize: '11px', color: '#999', fontWeight: '300' }}>Total Allowances</Text>
                      <Text style={{ fontSize: '12px', color: '#8B8B8D', fontWeight: '400' }}>
                        + Rs {formatCurrency(totalAllowances)}
                      </Text>
                    </div>
                    <Divider style={{ margin: '6px 0', borderColor: '#E8E6E3' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <Text strong style={{ fontSize: '12px', color: '#6B6B6D', fontWeight: '400' }}>Total</Text>
                      <Text strong style={{ fontSize: '13px', color: '#8B8B8D', fontWeight: '500' }}>
                        + Rs {formatCurrency(totalAllowances)}
                      </Text>
                    </div>
                  </Space>
                ) : (
                  <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <Text type="secondary" style={{ fontSize: '11px', color: '#C5C3C6', fontWeight: '300' }}>No allowances</Text>
                  </div>
                )}
              </div>
            </Col>

            {/* Deductions */}
            <Col span={12}>
              <div style={{
                background: '#8B8B8D',
                padding: '6px 12px',
                borderRadius: '2px 2px 0 0'
              }}>
                <Text strong style={{
                  color: 'white',
                  fontSize: '10px',
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase'
                }}>
                  Deductions
                </Text>
              </div>
              <div style={{
                border: '1px solid #E8E6E3',
                borderTop: 'none',
                borderRadius: '0 0 2px 2px',
                padding: '8px 12px',
                minHeight: '70px',
                background: 'white'
              }}>
                {/* Prioritize deduction details from payroll record, fallback to fetched deductions */}
                {deductionDetails.length > 0 ? (
                  <Space direction="vertical" size={3} style={{ width: '100%' }}>
                    {deductionDetails.map((deduction, index) => {
                      let description = deduction.name || 'Deduction';
                      if (deduction.is_percentage) {
                        description = `${deduction.name} (${deduction.original_amount}%)`;
                      }

                      return (
                        <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                          <Text style={{ fontSize: '11px', color: '#999', fontWeight: '300' }}>
                            {description}
                          </Text>
                          <Text style={{ fontSize: '12px', color: '#8B8B8D', fontWeight: '400' }}>
                            - Rs {formatCurrency(deduction.amount || 0)}
                          </Text>
                        </div>
                      );
                    })}
                    <Divider style={{ margin: '6px 0', borderColor: '#E8E6E3' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <Text strong style={{ fontSize: '12px', color: '#6B6B6D', fontWeight: '400' }}>Total</Text>
                      <Text strong style={{ fontSize: '13px', color: '#8B8B8D', fontWeight: '500' }}>
                        - Rs {formatCurrency(totalDeductions)}
                      </Text>
                    </div>
                  </Space>
                ) : deductions.length > 0 ? (
                  <Space direction="vertical" size={3} style={{ width: '100%' }}>
                    {deductions.map((deduction, index) => {
                      const amount = calculateDeductionAmount(deduction);
                      return (
                        <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                          <Text style={{ fontSize: '11px', color: '#999', fontWeight: '300' }}>
                            {deduction.deduction_types?.name || 'Deduction'}
                            {deduction.deduction_types?.is_percentage && ` (${deduction.amount}%)`}
                          </Text>
                          <Text style={{ fontSize: '12px', color: '#8B8B8D', fontWeight: '400' }}>
                            - Rs {formatCurrency(amount)}
                          </Text>
                        </div>
                      );
                    })}
                    <Divider style={{ margin: '6px 0', borderColor: '#E8E6E3' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <Text strong style={{ fontSize: '12px', color: '#6B6B6D', fontWeight: '400' }}>Total</Text>
                      <Text strong style={{ fontSize: '13px', color: '#8B8B8D', fontWeight: '500' }}>
                        - Rs {formatCurrency(totalDeductions)}
                      </Text>
                    </div>
                  </Space>
                ) : totalDeductions > 0 ? (
                  <Space direction="vertical" size={3} style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <Text style={{ fontSize: '11px', color: '#999', fontWeight: '300' }}>Total Deductions</Text>
                      <Text style={{ fontSize: '12px', color: '#8B8B8D', fontWeight: '400' }}>
                        - Rs {formatCurrency(totalDeductions)}
                      </Text>
                    </div>
                    <Divider style={{ margin: '6px 0', borderColor: '#E8E6E3' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <Text strong style={{ fontSize: '12px', color: '#6B6B6D', fontWeight: '400' }}>Total</Text>
                      <Text strong style={{ fontSize: '13px', color: '#8B8B8D', fontWeight: '500' }}>
                        - Rs {formatCurrency(totalDeductions)}
                      </Text>
                    </div>
                  </Space>
                ) : (
                  <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <Text type="secondary" style={{ fontSize: '11px', color: '#C5C3C6', fontWeight: '300' }}>No deductions</Text>
                  </div>
                )}
              </div>
            </Col>
          </Row>

          {/* Pay Summary */}
          <div style={{
            background: 'linear-gradient(135deg, #8B8B8D 0%, #A5A3A6 100%)',
            padding: '12px 12px',
            borderRadius: '2px',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: 'rgba(255, 255, 255, 0.3)'
            }}></div>

            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Text style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '11px', fontWeight: '300' }}>Base Earnings</Text>
                <Text style={{ color: 'white', fontSize: '12px', fontWeight: '400' }}>
                  Rs {formatCurrency(totalEarnings)}
                </Text>
              </div>

              {totalDeductions > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <Text style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '11px', fontWeight: '300' }}>- Deductions</Text>
                  <Text style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '12px', fontWeight: '400' }}>
                    Rs {formatCurrency(totalDeductions)}
                  </Text>
                </div>
              )}

              {totalAllowances > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <Text style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '11px', fontWeight: '300' }}>+ Allowances</Text>
                  <Text style={{ color: 'white', fontSize: '12px', fontWeight: '400' }}>
                    Rs {formatCurrency(totalAllowances)}
                  </Text>
                </div>
              )}

              <Divider style={{ margin: '6px 0', borderColor: 'rgba(255, 255, 255, 0.2)' }} />

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Text strong style={{ color: 'white', fontSize: '12px', fontWeight: '400', letterSpacing: '0.5px' }}>Gross Pay</Text>
                <Text strong style={{ color: 'white', fontSize: '13px', fontWeight: '500' }}>
                  Rs {formatCurrency(employee.gross_pay || 0)}
                </Text>
              </div>

              <Divider style={{ margin: '6px 0', borderColor: 'rgba(255, 255, 255, 0.3)' }} />

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                padding: '4px 0'
              }}>
                <Text strong style={{
                  color: 'white',
                  fontSize: '13px',
                  fontWeight: '400',
                  letterSpacing: '2px',
                  textTransform: 'uppercase'
                }}>Net Pay</Text>
                <Text strong style={{
                  color: 'white',
                  fontSize: '18px',
                  fontWeight: '300',
                  letterSpacing: '-0.5px'
                }}>
                  Rs {formatCurrency(employee.net_pay || 0)}
                </Text>
              </div>
            </Space>
          </div>

          {/* Footer */}
          <div style={{
            marginTop: '10px',
            paddingTop: '8px',
            borderTop: '1px solid #E8E6E3',
            textAlign: 'center'
          }}>
            <Text type="secondary" style={{
              fontSize: '9px',
              display: 'block',
              color: '#C5C3C6',
              fontWeight: '300',
              marginBottom: '2px'
            }}>
              This is a computer-generated payslip. No signature is required.
            </Text>
            <Text type="secondary" style={{
              fontSize: '9px',
              color: '#C5C3C6',
              fontWeight: '300',
              letterSpacing: '0.5px'
            }}>
              Generated on {moment().format('DD/MM/YYYY HH:mm')}
            </Text>
          </div>
        </div>
      </div>
      </ConfigProvider>
    </Modal>
  );
};

export default PayslipPreview;